<template>
    <q-card class="notification" bordered flat>
        <q-expansion-item
            expand-separator
            v-model="triggerExpansion"
            class="background-yellow"
            expand-icon-class="text-darker-yellow"
        >
            <template v-slot:header>
                <div class="row full-width">
                    <div v-if="asset" class="text-darker-yellow q-ml-xs">
                        <div class="col-md-12">
                            <span class="text-h6 text-bold">
                                {{ asset.name }}
                            </span>
                        </div>
                        <div class="col-md-4">
                            <span>
                                {{ receivedAt }} - {{ $t('Low Quality') }}
                            </span>
                        </div>
                    </div>
                    <div v-else class="col-md-12">
                        <span class="text-h6">
                            {{
                                isLoading
                                    ? $t('Loading notification details...')
                                    : $t('Failed to load quality notification')
                            }}
                        </span>
                    </div>
                </div>
            </template>
            <template v-slot:default>
                <q-card class="notification">
                    <!-- Reload banner -->
                    <q-card-section v-if="showReloadBanner">
                        <q-banner inline-actions class="text-black bg-white">
                            {{ $t('Click the reload button to try again.') }}
                            <template v-slot:action>
                                <q-btn
                                    @click="initialize"
                                    :loading="isLoading"
                                    :label="$t('Reload')"
                                    color="negative"
                                />
                            </template>
                        </q-banner>
                    </q-card-section>

                    <!-- Body (loading) -->
                    <q-card-section
                        v-if="!asset && isLoading"
                        class="text-center text-medium-grey"
                    >
                        <q-spinner color="primary" size="3em" :thickness="10" />
                        <br />
                        <p class="q-mt-md">
                            {{ $t('Loading notification details...') }}
                        </p>
                    </q-card-section>

                    <q-card-section
                        v-if="
                            asset &&
                            acceptableRejectPercentage &&
                            unitSampleSize &&
                            hasDefects
                        "
                    >
                        <span class="text-medium-grey">
                            {{
                                $t(
                                    'More than {rejectedPercentage}% of the last {unitSampleSize} units have been rejected.',
                                    {
                                        rejectedPercentage:
                                            acceptableRejectPercentage,
                                        unitSampleSize: unitSampleSize
                                    }
                                )
                            }}
                        </span>
                    </q-card-section>

                    <!-- Body (loaded) -->
                    <q-card-section v-if="asset" class="q-px-lg q-pt-none">
                        <q-form @submit="submit" v-show="hasDefects">
                            <!-- Body (loaded) -->
                            <defect-table-select
                                v-if="qualitySelectProps.url"
                                v-model="selectedQualityDefect"
                                @confirmSelection="
                                    selectedQualityDefect =
                                        qualitySelectProps.selection
                                "
                                :baseURL="qualitySelectProps.url"
                                :queryParams="qualitySelectProps.queryParams"
                                :label="qualitySelectProps.label"
                                :dialogTitle="qualitySelectProps.dialogTitle"
                                :searchLabel="qualitySelectProps.searchLabel"
                                :facilityId="qualitySelectProps.facilityId"
                                :rules="[val => !!val || $t('Select a Defect')]"
                                :dense="true"
                                :fontSize="'large'"
                                row-key="id"
                                class="q-pt-sm notification-header"
                                @hasItems="
                                    qualitySelectProps.hasDefects = $event
                                "
                            >
                                <template v-slot:item="data">
                                    <defect-card
                                        @select="
                                            qualitySelectProps.selection =
                                                data.props.row
                                        "
                                        :name="data.props.row.name"
                                        :code="data.props.row.code"
                                        :description="
                                            data.props.row.description
                                        "
                                        :isSelected="
                                            qualitySelectProps.selection &&
                                            qualitySelectProps.selection.id ===
                                                data.props.row.id
                                        "
                                        :isSelectable="true"
                                    />
                                </template>
                                <template v-slot:label>
                                    {{ $t('Select a Defect') }}
                                </template>
                            </defect-table-select>
                            <div
                                class="q-mt-lg"
                                :class="{
                                    'button-container': $q.screen.gt.xs
                                }"
                            >
                                <q-btn
                                    @click="dismiss"
                                    class="q-mr-md"
                                    color="white"
                                    text-color="secondary"
                                    outline
                                    v-show="allowDismiss"
                                    size="1.2rem"
                                    :class="
                                        $q.screen.xs ? 'full-width q-mt-sm' : ''
                                    "
                                    >{{ $t('Dismiss') }}
                                </q-btn>
                                <q-btn
                                    type="submit"
                                    :disable="
                                        $userCannot('edit', 'quality_events')
                                    "
                                    :class="{
                                        'full-width q-mt-sm': $q.screen.xs
                                    }"
                                    :loading="isLoading"
                                    color="primary"
                                    size="1.2rem"
                                    outline
                                >
                                    {{ $t('Resolve') }}
                                </q-btn>
                            </div>
                        </q-form>
                        <div v-if="!hasDefects" class="row q-pa-md text-center">
                            <div class="q-mt-md text-center full-width">
                                <q-icon
                                    name="fas fa-exclamation-triangle"
                                    size="md"
                                    class="text-center"
                                    color="secondary"
                                />
                            </div>
                            <div class="q-my-md text-body1 text-secondary">
                                {{
                                    $t(
                                        'This asset currently has no quality defects linked to it. To ensure quality events can be properly justified, please add quality defects to this asset.'
                                    )
                                }}
                            </div>
                            <q-space />
                            <q-btn
                                @click="dismiss"
                                color="white"
                                text-color="secondary"
                                outline
                                size="1.2rem"
                                :class="{
                                    'full-width q-mt-sm': $q.screen.xs
                                }"
                            >
                                {{ $t('Dismiss') }}
                            </q-btn>
                        </div>
                    </q-card-section>
                </q-card>
            </template>
        </q-expansion-item>
    </q-card>
</template>

<script>
import DefectCard from '@components/DefectCard.vue';
import DefectTableSelect from '@components/DefectTableSelect.vue';
import { getLocaleFormat, getTime } from '@models/timeformats.js';
import { mapGetters } from 'vuex';
import { useNotificationsStore } from '@stores/notifications.ts';
import { mapActions } from 'pinia';

export default {
    name: 'QualityNotificationCard',
    components: { DefectCard, DefectTableSelect },
    props: {
        notification: {
            validator(value) {
                return typeof value === 'object' || value === null;
            },
            required: false
        },
        index: {
            type: Number,
            required: false
        }
    },
    async mounted() {
        await this.initialize();
    },
    data() {
        return {
            asset: null,
            proximityReading: null,
            selectedQualityDefect: null,

            // quality selector
            qualitySelectProps: {
                url: 'defects/',
                queryParams: { order: 'name' },
                label: this.$t('Assign a quality defect to this event'),
                dialogTitle: this.$t('Select a Defect'),
                searchLabel: this.$t('Search a defect'),
                selection: null,
                facilityId: null,
                hasDefects: true
            },
            initSuccess: null,
            isLoading: false,
            expand: null,
            loadingDefect: false,
            defectName: ''
        };
    },
    computed: {
        ...mapGetters({
            getAssetByUUID: 'facilities/getAssetByUUID'
        }),
        /**
         * Show reload banner.
         * Only show if an attempt to load has been made and there was a failure.
         * @return {Boolean}
         */
        showReloadBanner() {
            if (this.initSuccess === null) {
                return false;
            } else {
                return !this.initSuccess && !this.isLoading;
            }
        },
        /**
         * Allow dismiss notification popup
         */
        allowDismiss() {
            // check if the user is always allowed to dismiss
            if (this.$userCan('always_dismiss', 'quality_notifications')) {
                return true;
            }
            return false;
        },
        /**
         * Open expansion based on index of notification
         * if the notification is at the 0th index then it is at the top of the list
         * and should be open
         */
        triggerExpansion: {
            get() {
                return this.expand === null
                    ? this.index === 0 || this.expand
                    : this.expand;
            },
            set(val) {
                this.expand = val;
                return this.expand;
            }
        },
        /**
         * Get quality event UUID.
         * @return {String} - UUID.
         */
        qualityEventUUID() {
            return this.notification ? this.notification.eventUUID : null;
        },
        /**
         * Get acceptable reject percentage for the asset.
         * @return {String} - Acceptable reject percentage.
         */
        acceptableRejectPercentage() {
            return this.asset ? this.asset.acceptable_percentage_rejects : null;
        },
        /**
         * Get acceptable unit sample size for the asset.
         * @return {String} - Acceptable unit sample size.
         */
        unitSampleSize() {
            return this.asset ? this.asset.acceptable_reject_sample_size : null;
        },
        /**
         * Check if the asset has defects.
         */
        hasDefects() {
            return this.qualitySelectProps.hasDefects;
        },
        /**
         * Get the received date of the notification.
         * @return {String} - Received date or time.
         */
        receivedAt() {
            if (this.isReceivedToday) {
                return getTime(this.notification['receivedAt']);
            }
            return getLocaleFormat(
                this.notification['receivedAt'],
                this.userLocale
            );
        },
        /**
         * Check if the notification received date is today.
         * @return {Boolean} - True if received today.
         */
        isReceivedToday() {
            return (
                new Date(this.notification['receivedAt']).toDateString() ===
                new Date().toDateString()
            );
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [
            'dismissQualityNotificationAction'
        ]),
        /**
         * Initialize component.
         */
        async initialize() {
            // check if asset details can be fetched from the store
            this.asset = this.getAssetByUUID(this.notification['assetUUID']);

            // if asset is not in store, fetch it from the server
            if (this.asset === null) {
                this.asset = await this.fetchAssetFromAPI();
            }

            // initialize component if asset was loaded
            if (this.asset) {
                // initialize url to use for defect selector
                this.qualitySelectProps.url = `/assets/${this.asset.id}/defects/`;
                this.qualitySelectProps.facilityId = this.asset.facility;

                this.initSuccess = true;
            } else {
                this.initSuccess = false;
            }
        },
        async fetchAssetFromAPI() {
            this.isLoading = true;
            let asset = null;
            try {
                const response = await this.$axios.get(
                    `assets/?uuid=${this.notification['assetUUID']}`
                );
                if (response.status === 200) {
                    if (response.data.results) {
                        asset = response.data.results[0];
                    }
                } else {
                    console.error('Failed to fetch asset due to: ');
                }
            } catch (error) {
                console.error('Failed to fetch asset due to: ', error);
            }
            this.isLoading = false;
            return asset;
        },
        /**
         * Handle submit.
         */
        async submit() {
            this.isLoading = true;
            // After making the API call, the card closes right away,
            // which means that this.$t will not be accessible, resulting in an error being thrown.
            const successMessage = this.$t('Defect assigned to quality event');
            const errorMessage = this.$t(
                'Failed to submit event. Please try again later'
            );

            try {
                // find the quality event id from the notification object
                const eventUUID = this.notification['eventUUID'];
                const qualityDefectID =
                    this.selectedQualityDefect['id'] || null;

                if (eventUUID && qualityDefectID) {
                    const response = await this.$axios.post(
                        `quality_events/justify/`,
                        {
                            uuid: eventUUID,
                            defect_id: qualityDefectID
                        }
                    );
                    if (response.status === 200) {
                        // Display success notification when
                        // user press the submit button after
                        // entering the quality defect and
                        // description of the downtime event.
                        this.$notifySuccess(successMessage);
                    }

                    // Dismiss the notification after submitting the quality defect
                    await this.dismiss();
                } else {
                    this.$notifyError(errorMessage);
                }
            } catch (error) {
                this.$notifyError(errorMessage, error);
            }
            this.isLoading = false;
        },
        /**
         * Dismiss downtime notification
         */
        async dismiss() {
            try {
                this.dismissQualityNotificationAction(this.qualityEventUUID);
            } catch (error) {
                console.error('Error dismissing notification due to: ', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.notification {
    max-width: 100%;
    border-width: 0.1rem;
    border-color: rgba(0, 0, 0, 0.12);
    font-size: 1rem;
}
.notification-header {
    height: 60px;
}

.button-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
}
.button {
    justify-content: flex-end;
}
.event-badge {
    font-size: 16px;
    padding: 4px 6px;
    margin-top: 5px;
}
</style>
